import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { setLoading, storeHeaderActions, storeSearchSerials } from 'store/reducers/mainSlice';
import { FaSearch } from 'react-icons/fa';
import Filter from 'ui-component/filter-menu/Filter';
import { Skeleton, Tooltip } from '@mui/material';
import { TbDatabaseExclamation } from 'react-icons/tb';
import usePageTitle from 'hooks/usePageTitle';
import SortableHeader from 'ui-component/SortableHeader';
import NoRecordFoundIcon from 'assets/images/icons/no-records-found.svg';
import useIsMobile from 'utils/isMobileUtils';
import SerialDetailsModal from './modal/detailBox.jsx';
import NoImage from 'assets/images/no-image.png';
import * as XLSX from 'xlsx';
import '../serials/modal/detailBox.scss';

// ✅ Clear
const SerialsIndex = () => {
	const [setPageTitle] = usePageTitle('Serials');
	useEffect(() => {
		return () => {
			setPageTitle(null);
		};
	}, []);
	const dispatch = useDispatch();
	const isMobile = useIsMobile();

	const [searchValue, setSearchValue] = useState();
	const [searchField, setSearchField] = useState(null);
	const [searchTimeout, setSearchTimeout] = useState(null);
	const [filters, setFilters] = useState([]);
	const [activeFilters, setActiveFilters] = useState({});

	const [sortByFilter, setSortByFilter] = useState({ field: 'created_at', direction: 'desc' });

	const [dataList, setDataList] = useState([]);
	const [page, setPage] = useState(1);
	const [hasMore, setHasMore] = useState(true);
	const [showPreloadingRows, setShowPreloadingRows] = useState(false);
	const [totalRecords, setTotalRecords] = useState(0);
	const [isLoading, setIsLoading] = useState(true);

	const [isModalOpen, setIsModalOpen] = useState(false);
	const [selectedSerial, setSelectedSerial] = useState(null);

	const tableRef = useRef();

	const handleRowClick = (item) => {

		setSelectedSerial({
			thumbnail_url: item?.product?.thumbnail_url || NoImage,
			product_name: item?.product?.name,
			model: item?.product?.sku,
			imei: item?.imei,
			invoice_number: item?.invoice_number,
			device_type: item?.product?.category_name,
			memory: item?.product?.memory,
			colors: item?.product?.color_name
		});

		setIsModalOpen(true);
	};
	useEffect(() => {
		setIsLoading(true);
	}, [activeFilters]);

	// --- Build Request (Reusable Logic) ---
	const buildRequest = useCallback(
		(pageNumber) => {
			const query = {
				page: pageNumber,
				page_length: isMobile ? 10 : 25,
				sortBy: sortByFilter?.field,
				sortOrder: sortByFilter?.direction,
				search: searchField || '',
				...activeFilters
			};

			Object.keys(query).forEach((key) => {
				let value = query[key];
				if (Array.isArray(value)) {
					value = value.join(',');
				}
				if (value !== null && typeof value === 'object' && !Array.isArray(value) && value.value !== undefined) {
					value = value.value;
				}

				query[key] = value;

				if (query[key] === null || query[key] === undefined || query[key] === '') {
					delete query[key];
				}
			});
			return query;
		},
		[searchField, sortByFilter, activeFilters, isMobile]
	);

	// --- Core Data Fetching: Initialization/Refresh (Standardized Logic) ---
	const initializeData = useCallback(() => {
		setIsLoading(true);
		setHasMore(true);
		setDataList([]);
		setPage(1);
		setShowPreloadingRows(true);

		const request = buildRequest(1);

		api
			.getClientSerial(request)
			.then((response) => {
				const meta = response?.data || {};
				const receivedData = meta?.data || [];

				setDataList(receivedData);
				setTotalRecords(meta?.total || 0);

				const nextPage = meta?.current_page || 1;
				const lastPage = meta?.last_page || nextPage;

				setPage(nextPage);
				setHasMore(nextPage < lastPage);
			})
			.catch((error) => console.error('Error fetching initial serials:', error))
			.finally(() => {
				dispatch(setLoading(false));
				setIsLoading(false);
				setShowPreloadingRows(false);
			});
	}, [searchField, sortByFilter, activeFilters, isMobile, dispatch, buildRequest]);

	// --- Core Data Fetching: Load More (Standardized Logic) ---
	const loadMoreData = useCallback(() => {
		if (showPreloadingRows || !hasMore) return;

		setShowPreloadingRows(true);
		const nextPage = page + 1;
		const request = buildRequest(nextPage);

		api
			.getClientSerial(request)
			.then((response) => {
				const meta = response?.data || {};
				const receivedData = meta?.data || [];

				const current_page = meta?.current_page || nextPage;
				const last_page = meta?.last_page || current_page;

				if (current_page > last_page || receivedData.length === 0) {
					setHasMore(false);
				} else {
					setDataList((prev) => [...prev, ...receivedData]);
					setPage(current_page);
				}
			})
			.catch((error) => console.error('Error loading more serials:', error))
			.finally(() => {
				setShowPreloadingRows(false);
			});
	}, [hasMore, page, showPreloadingRows, buildRequest]);

	// --- Handlers ---
	const handleExport = () => {
		if (!dataList || dataList.length === 0) {
			alert('No serials to export');
			return;
		}

		const headers = ['Product Name', 'IMEI', 'Invoice Number'];

		const exportData = dataList.map((item) => ({
			'Product Name': item?.product?.name || 'N/A',
			IMEI: item?.imei || 'N/A',
			'Invoice Number': item?.invoice_number || 'N/A'
		}));

		const worksheet = XLSX.utils.json_to_sheet(exportData, {
			header: headers,
			skipHeader: false
		});

		const actualRowCount = exportData.length + 1;

		if (worksheet['!ref']) {
			const range = XLSX.utils.decode_range(worksheet['!ref']);
			range.e.r = actualRowCount - 1;
			range.e.c = 2;

			worksheet['!ref'] = XLSX.utils.encode_range(range);
		}
		const wscols = [{ wch: 30 }, { wch: 20 }, { wch: 20 }];
		worksheet['!cols'] = wscols;

		const workbook = XLSX.utils.book_new();
		XLSX.utils.book_append_sheet(workbook, worksheet, 'Serials');
		XLSX.writeFile(workbook, 'ProjectMix-Serials.xlsx');
	};

	const getFiltersList = () => {
		api.getClientSerialFilter().then((res) => {
			setFilters(res.data);
		});
	};

	const handleSearch = (e) => {
		let text = e.target.value;

		setSearchValue(text);
		if (searchTimeout) clearTimeout(searchTimeout);

		let timeout = setTimeout(() => {
			dispatch(storeSearchSerials(text));
			setSearchField(text); // Triggers data initialization
		}, 500);

		setSearchTimeout(timeout);
	};

	const updateSortByStatus = (name) => {
		setIsLoading(true);
		if (sortByFilter?.field === name) {
			setSortByFilter((e) => ({
				...e,
				direction: e?.direction === 'asc' ? 'desc' : 'asc'
			}));
		} else {
			setSortByFilter({
				field: name,
				direction: 'asc'
			});
		}
	};

	// --- Effects ---

	useEffect(() => {
		initializeData();
		getFiltersList(); 
	}, [initializeData]);

	useEffect(() => {
		dispatch(storeHeaderActions({ showQuickStats: false, showExport: true }));
	}, [dispatch]);

	useEffect(() => {
		const handleScroll = () => {
			if (!tableRef.current || showPreloadingRows || !hasMore) return;
			const { scrollTop, scrollHeight, clientHeight } = tableRef.current;
			if (scrollTop + clientHeight >= scrollHeight - 10) {
				loadMoreData();
			}
		};

		const currentRef = tableRef.current;
		currentRef?.addEventListener('scroll', handleScroll);

		return () => currentRef?.removeEventListener('scroll', handleScroll);
	}, [showPreloadingRows, hasMore, loadMoreData]);

	useEffect(() => {
		if (isMobile) {
			document.body.style.overflow = 'hidden';
		} else {
			document.body.style.overflow = 'auto';
		}

		return () => {
			document.body.style.overflow = 'auto';
		};
	}, [isMobile]);

	return (
		<>
			<div className="row">
				<div className="col-xl-12 col-lg-12">
					<div className="show-on-mobile new-page-header" style={{ position: 'sticky', top: 0, zIndex: 100, background: 'white' }}>
						{' '}
						<div className="client-header-bubble" />
						<div className="d-flex flex-column gap-2" style={{ position: 'relative', zIndex: 1, paddingTop: 10, width: '100%' }}>
							<h5 style={{ textAlign: 'center' }}>Serials</h5>
							<div
								style={{
									paddingLeft: 20,
									paddingRight: 20,
									display: 'flex',
									justifyContent: 'center',
									alignItems: 'center',
									gap: 10,
									paddingBottom: 4
								}}
							/>
							<div
								className="header-option-container"
								style={{ display: 'flex', gap: 10, justifyContent: 'center', width: 'max-content', margin: '0 auto' , backgroundColor: 'transparent' , overflow: 'visible'}}
							>
								<Tooltip title="Export Data">
									<button
										className="header-option active btn btn-outlined-main-primary white-var"
										style={{ whiteSpace: 'nowrap', padding: '6px 10px', height: '29px' }}
										onClick={handleExport}
									>
										<span>Export</span>
									</button>
								</Tooltip>
							</div>
						</div>
					</div>

					<div
						className="show-on-mobile"
						style={{
							display: 'flex',
							justifyContent: 'space-between',
							alignItems: 'center',
							gap: 10,
							paddingTop: 0,
							paddingBottom: 5,
							paddingLeft: 20,
							paddingRight: 20
						}}
					>
						<form className="form-inline navbar-search" style={{ flex: 1 }}>
							<div style={{ position: 'relative', width: '100%' }}>
								<input
									type="text"
									className="form-control bg-white border"
									placeholder="Search"
									aria-label="Search"
									aria-describedby="basic-addon2"
									value={searchValue || ''}
									onChange={handleSearch}
									style={{ width: '100%', paddingRight: 36 }}
								/>
								<span
									style={{
										position: 'absolute',
										right: 10,
										top: '50%',
										transform: 'translateY(-50%)',
										color: '#888',
										pointerEvents: 'none'
									}}
								>
									<FaSearch />
								</span>
							</div>
						</form>
						<div style={{ flexShrink: 0 }}>
							<Filter
								activeFilters={activeFilters}
								onSubmit={setActiveFilters}
								filters={filters}
								source={`serials`}
								multi={undefined}
								noScrolling={true}
							/>
						</div>
					</div>
					<div className="hide-on-mobile">
						<div className="flex justify-between mobile:flex-col mobile:items-start" style={{ marginTop: '10px' }}>
							<h5
								style={{
									color: '#17181A',
									fontWeight: 600,
									marginTop: '10px',
									marginLeft: '5px'
								}}
							>
								Serials
							</h5>

							<Tooltip title="Export Data">
								<button
									className="btn btn-outlined-main-primary white-va mobile:ml-auto"
									style={{ whiteSpace: 'nowrap', marginLeft: '284px' }}
									onClick={handleExport}
								>
									Export
								</button>
							</Tooltip>
						</div>
					</div>
					<div
						className={`card shadow-sm  bg-white table-card ${Object.keys(activeFilters).length !== 0 ? 'has-filter' : ''} mt-3 ${
							isMobile ? 'shorter-table' : ''
						}`}
					>
						<div className="align-items-center justify-content-between TableSerachBar">
							<Filter
								activeFilters={activeFilters}
								onSubmit={setActiveFilters}
								filters={filters}
								source={`serials`}
								multi={undefined}
								noScrolling={true}
							/>
							<form className="form-inline navbar-search" style={{ display: 'flex', gap: '5px' }} onSubmit={(e) => e.preventDefault()}>
								<div className="input-group">
									<input
										type="text"
										className="form-control bg-white border"
										placeholder="Search"
										value={searchValue || ''}
										onChange={handleSearch}
									/>
									<span className="searchIcon">
										<FaSearch />
									</span>
								</div>
							</form>
						</div>

						<div className="estimation-table-area">
							<div className="table-side">
								{!isMobile ? (
									<div
										className="table-responsive-xl tableData estimationList mt-3"
										ref={tableRef}
										style={{
											height: 'calc(100vh - 312px)',
											overflowY: 'auto',
											overflowY: 'auto',
											scrollbarWidth: 'thin',
											scrollbarColor: '#0966ff transparent',
											marginTop: '36px'
										}}
									>
										<table className="table min-width-unset tbl-space pm-table d-none d-md-table">
											<thead>
												<tr className="sticky-header">
													<th scope="col" style={{ width: '50px' }}></th>
													<th>
														<SortableHeader
															name="product_name"
															currentSortBy={sortByFilter}
															onClick={() => updateSortByStatus('product_name')}
														>
															Product
														</SortableHeader>
													</th>
													<th>
														<SortableHeader name="imei" currentSortBy={sortByFilter} onClick={() => updateSortByStatus('imei')}>
															IMEIS
														</SortableHeader>
													</th>
													<th>
														<SortableHeader
															name="invoice_number"
															currentSortBy={sortByFilter}
															onClick={() => updateSortByStatus('invoice_number')}
														>
															Invoice
														</SortableHeader>
													</th>
												</tr>
											</thead>
											<tbody>
												{/* --- Desktop Loading State --- */}
												{isLoading &&
													[...Array(50)].map((_, i) => (
														<tr key={i}>
															{[...Array(4)].map(
																(
																	_,
																	j // Changed 7 to 4 columns for this table structure
																) => (
																	<td key={j}>
																		<Skeleton
																			variant="rounded"
																			width={j === 0 ? '50px' : '100%'} // Smaller width for image column
																			height={j === 0 ? '50px' : '15px'}
																			style={{ borderRadius: j === 0 ? '4px' : '32px' }}
																		/>
																	</td>
																)
															)}
														</tr>
													))}

												{/* --- Desktop Data Display --- */}
												{!isLoading && dataList.length > 0
													? dataList.map((item, index) => (
															<tr key={index} className="cursor-pointer" onClick={() => handleRowClick(item)}>
																<td className="align-middle">
																	<img
																		src={item?.product?.thumbnail_url || NoImage}
																		style={{ width: '30px', height: '50px', objectFit: 'contain' }}
																		alt="No image"
																	/>
																</td>
																<td>
																	<div className="d-flex align-items-center gap-2">
																		<p className="text-black fw-500 " style={{ marginTop: '16px' }}>
																			{item?.product?.name || 'Product Missing'}
																		</p>
																	</div>
																</td>
																<td style={{ color: '#17181A' }}>{item?.imei || 'N/A'}</td>
																<td>
																	<span style={{ color: '#17181A' }}>{item?.invoice_number || 'N/A'}</span>
																</td>
															</tr>
													  ))
													: !isLoading && (
															<tr>
																<td colSpan={4}>
																	<div className="not-found-container">
																		<div className="not-found-icon">
																			<img src={NoRecordFoundIcon} style={{ height: '200px', width: '200px' }} alt="No records found" />
																		</div>
																		<h6 className="not-found-text">No Serials Found</h6>
																	</div>
																</td>
															</tr>
													  )}

												{/* --- Desktop Preloading Rows (Infinite Scroll) --- */}
												{showPreloadingRows &&
													!isLoading &&
													hasMore &&
													[...Array(50)].map((_, i) => (
														<tr key={`preload-${i}`}>
															{[...Array(4)].map((_, j) => (
																<td key={j}>
																	<Skeleton variant="rounded" width="100%" height={15} style={{ borderRadius: '32px' }} />
																</td>
															))}
														</tr>
													))}
											</tbody>
										</table>
									</div>
								) : (
									<div
										className="table-responsive-xl tableData estimationList"
										ref={tableRef}
										style={{
											height: `calc(100dvh - ${Object.keys(activeFilters).length === 0 ? '313px' : '300px'})`,
											overflowY: 'auto',
											display: 'flex',
											flexDirection: 'column'
											// padding: '0 20px'
										}}
									>
										{/* --- Mobile Loading State --- */}
										{isLoading ? (
											<div className="inv-cards-list d-md-none">
												{[...Array(15)].map((_, idx) => (
													<div
														key={idx}
														className="order-list-card"
														style={{
															background: idx % 2 === 0 ? '#f9f9f9' : '#fff',
															borderRadius: 12,
															padding: 16,
															display: 'flex',
															flexDirection: 'column',
															gap: 10,
															marginBottom: 10
														}}
													>
														{[...Array(3)].map((__, i) => (
															<Skeleton key={i} variant="rounded" width={'100%'} height={18} style={{ borderRadius: '8px' }} />
														))}
													</div>
												))}
											</div>
										) : (
											<div className="inv-cards-list d-md-none">
												{/* --- Mobile Data Display --- */}
												{dataList.length > 0
													? dataList.map((item, index) => (
															<div
																className="order-list-card active-action"
																key={index}
																style={{
																	background: index % 2 === 0 ? '#fff' : '#fff',
																	marginBottom: 10,
																	display: 'grid',
																	gridTemplateColumns: '1fr 5fr',
																	alignItems: 'center',
																	justifyContent: 'unset',
																	borderRadius: '0.5rem',
																	gap: 10
																}}
																onClick={() => handleRowClick(item)}
															>
																<div>
																	<img
																		src={item?.product?.thumbnail_url || NoImage}
																		alt="product"
																		style={{ width: 'auto', height: 60, objectFit: 'cover', borderRadius: 8 }}
																	/>
																</div>
																<div style={{ flex: 1 }}>
																	<h6>
																		<div className="fw-500 text-black">{item?.product?.name || 'ProductName Missing'}</div>
																	</h6>
																	<div>
																		IMEIS: <span style={{ color: '#17181A' }}>{item?.imei || 'N/A'}</span>
																	</div>
																	<div>
																		Invoice: <span style={{ color: '#17181A' }}>{item?.invoice_number || 'N/A'}</span>
																	</div>
																</div>
															</div>
													  ))
													: !isLoading && (
															<div className="w-100">
																<div className="not-found-container">
																	<div className="not-found-icon">
																		<TbDatabaseExclamation style={{ height: '200px', width: '200px' }} />
																	</div>
																	<h6 className="not-found-text">No Serials Available</h6>
																</div>
															</div>
													  )}

												{/* --- Mobile Preloading Rows (Infinite Scroll) --- */}
												{showPreloadingRows &&
													!isLoading &&
													hasMore &&
													[...Array(5)].map((_, idx) => (
														<div
															key={`preload-mobile-${idx}`}
															className="order-list-card"
															style={{
																background: idx % 2 === 0 ? '#f9f9f9' : '#fff',
																borderRadius: 12,
																padding: 16,
																display: 'flex',
																flexDirection: 'column',
																gap: 10,
																marginBottom: 10
															}}
														>
															{[...Array(3)].map((__, i) => (
																<Skeleton key={i} variant="rounded" width={'100%'} height={18} style={{ borderRadius: '8px' }} />
															))}
														</div>
													))}
											</div>
										)}
									</div>
								)}

								{/* Total Records Display */}
								<div
									className="hide-on-mobile"
									style={{
										padding: '10px 0 0 20px',
										borderTop: '1px solid #e0e0e0e0',
										display: 'flex',
										gap: '5px',
										justifyContent: 'flex-end',
										height: '40px',
										marginRight: '24px'
									}}
								>
									<div className="text-gray-800">Total Record:</div>
									<div className="fw-500">
										{isLoading ? (
											<>
												<div style={{ width: '100px' }}>
													<Skeleton variant="rounded" width={'100%'} height={20}></Skeleton>
												</div>
											</>
										) : (
											<>{totalRecords.toLocaleString('en-US')}</>
										)}
									</div>
								</div>

								{isModalOpen && (
									<div
										className={`modal-blur-overlay ${isModalOpen ? 'open' : ''}`}
										onClick={() => setIsModalOpen(false)}
										aria-hidden="true"
									/>
								)}
								{!isMobile ? (
									<SerialDetailsModal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} selectedSerial={selectedSerial} />
								) : (
									<SerialDetailsModal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} selectedSerial={selectedSerial} />
								)}
							</div>
						</div>
					</div>
				</div>
			</div>
		</>
	);
};

export default SerialsIndex;